import React, { useState, useEffect } from 'react'
import { Card, Form, Input, Select, Button, Spin, Typography, Divider, Space, Switch, Alert, App } from 'antd'
import { PageLoading } from '@/shared/components/ui'
import { useTranslation } from 'react-i18next'
import { DatabaseOutlined, ApiOutlined, SaveOutlined, LinkOutlined, LoadingOutlined } from '@ant-design/icons'
import { orderConfigApi, StorageConfigRequest } from '@/shared/api/orderConfigApi'
import SensitiveFieldInput from '../settings/components/SensitiveFieldInput'

const { Title, Text, Paragraph } = Typography
const { Option } = Select

interface OrderConfigPageProps {
  companyId: number
}

// 第三步：增强数据映射和验证的类型定义
interface DatabaseConfig {
  host: string
  port: number
  database: string
  username: string
  password?: string
  schema?: string
  ssl_mode?: string
  charset?: string
}

interface ApiConfig {
  base_url: string
  api_key: string
  auth_type: string
  timeout: number
  retries: number
}

interface OrderConfig {
  type: 'postgres' | 'mysql' | 'rest_api'
  config: DatabaseConfig | ApiConfig
  is_default: boolean
  enabled: boolean
}

// 表单值接口
interface OrderFormValues {
  dataSourceType: 'postgres' | 'mysql' | 'rest_api'
  host?: string
  port?: number
  database?: string
  username?: string
  password?: string
  schema?: string
  ssl_mode?: string
  charset?: string
  apiUrl?: string
  apiKey?: string
  authType?: string
  timeout?: number
  retries?: number
  syncFrequency?: string
  errorRetryCount?: number
  is_default?: boolean
  enabled?: boolean
}

const OrderConfigPage: React.FC<OrderConfigPageProps> = ({ companyId }) => {
  const { t } = useTranslation(['modules', 'common'])
  const [form] = Form.useForm()
  const { message: messageApi } = App.useApp()
  const [loading, setLoading] = useState(false)
  const [dataSourceType, setDataSourceType] = useState<'postgres' | 'mysql' | 'rest_api'>('postgres')
  const [configState, setConfigState] = useState<'loading' | 'loaded' | 'unconfigured' | 'error'>('loading')

  // 缓存配置
  const CACHE_KEY = `order_config_${companyId}`
  const CACHE_TIMEOUT = 60 * 60 * 1000 // 1小时

  const getCachedConfig = (): any | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) return null

      const { data, timestamp } = JSON.parse(cached)
      // 检查缓存是否过期
      if (Date.now() - timestamp > CACHE_TIMEOUT) {
        localStorage.removeItem(CACHE_KEY)
        return null
      }
      return data
    } catch (error) {
      console.error('读取缓存失败:', error)
      return null
    }
  }

  const saveToCache = (config: any) => {
    try {
      const cacheData = {
        data: config,
        timestamp: Date.now()
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData))
    } catch (error) {
      console.error('保存缓存失败:', error)
    }
  }

  const clearCache = () => {
    try {
      localStorage.removeItem(CACHE_KEY)
    } catch (error) {
      console.error('清除缓存失败:', error)
    }
  }

  const handleDataSourceTypeChange = (value: 'postgres' | 'mysql' | 'rest_api') => {
    setDataSourceType(value)
  }

  // 加载现有配置（集成缓存机制）
  useEffect(() => {
    const loadConfig = async () => {
      if (!companyId) return

      // 在try块外部声明cachedConfig，以便catch块可以访问
      let cachedConfig = null

      try {
        // 1. 首先检查缓存，立即显示缓存数据（如果有）
        cachedConfig = getCachedConfig()
        if (cachedConfig) {
          const cachedFormValues = mapConfigToForm(cachedConfig)
          form.setFieldsValue(cachedFormValues)
          setDataSourceType(cachedConfig.type || 'postgres')
          setConfigState('loaded') // 暂时标记为已加载，后续API调用会更新
        }

        // 2. 发起API请求获取最新数据
        const response = await orderConfigApi.getConfig(companyId)

        if (response?.success) {
          if (response.data) {
            // 有配置数据：映射到表单
            const configData = response.data
            const formValues = mapConfigToForm(configData)

            // 3. 更新缓存（使用最新数据）
            saveToCache(configData)

            // 4. 如果缓存数据和API数据不同，更新表单
            if (!cachedConfig || JSON.stringify(cachedConfig) !== JSON.stringify(configData)) {
              form.setFieldsValue(formValues)
              setDataSourceType(configData.type || 'postgres')
            }

            setConfigState('loaded')
          } else {
            // 无配置数据：显示"未配置"状态
            setConfigState('unconfigured')
            // 清除可能存在的过时缓存
            clearCache()
          }
        } else {
          // API调用失败
          // 如果有缓存数据，保持'loaded'状态，否则显示错误
          if (!cachedConfig) {
            setConfigState('error')
          }
          // 如果有缓存，状态保持'loaded'（已在第129行设置）
        }
      } catch (error) {
        console.error('配置加载失败:', error)
        // 如果无缓存数据，则显示错误状态
        if (!cachedConfig) {
          setConfigState('error')
        }
        // 如果有缓存，状态保持'loaded'（已在第129行设置）
      }
    }

    loadConfig()
  }, [companyId, form])

  // 构建存储配置请求
  const buildStorageConfigRequest = (formValues: any): StorageConfigRequest => {
    const config: Record<string, any> = {}

    if (dataSourceType === 'postgres' || dataSourceType === 'mysql') {
      config.host = formValues.host || 'localhost'
      config.port = formValues.port || (dataSourceType === 'postgres' ? 5432 : 3306)
      config.database = formValues.database || 'default_orders'
      config.username = formValues.username || ''
      config.password = formValues.password || ''
      if (dataSourceType === 'postgres') {
        config.schema = formValues.schema || 'public'
        config.ssl_mode = formValues.ssl_mode || 'prefer'
      } else {
        config.charset = formValues.charset || 'utf8mb4'
      }
    } else if (dataSourceType === 'rest_api') {
      config.base_url = formValues.apiUrl || ''
      config.api_key = formValues.apiKey || ''
      config.auth_type = formValues.authType || 'bearer'
      config.timeout = formValues.timeout || 30
      config.retries = formValues.retries || 3
    }

    return {
      type: dataSourceType,
      config,
      is_default: formValues.is_default !== false,
      enabled: formValues.enabled !== false,
    }
  }

  // 配置验证工具
  const validateConfig = (config: any): { valid: boolean; errors: string[] } => {
    const errors: string[] = []

    if (!config) {
      errors.push('配置数据为空')
      return { valid: false, errors }
    }

    if (!config.type) {
      errors.push('缺少配置类型')
    } else if (!['postgres', 'mysql', 'rest_api'].includes(config.type)) {
      errors.push(`无效的配置类型: ${config.type}`)
    }

    if (!config.config) {
      errors.push('缺少配置详情')
    } else {
      // 类型特定验证
      if (config.type === 'postgres' || config.type === 'mysql') {
        const dbConfig = config.config
        if (!dbConfig.host) errors.push('数据库主机不能为空')
        if (!dbConfig.database) errors.push('数据库名称不能为空')
        if (!dbConfig.username) errors.push('数据库用户名不能为空')
      } else if (config.type === 'rest_api') {
        const apiConfig = config.config
        if (!apiConfig.base_url) errors.push('API地址不能为空')
        if (!apiConfig.api_key) errors.push('API密钥不能为空')
      }
    }

    return {
      valid: errors.length === 0,
      errors
    }
  }

  // 将配置数据映射到表单（增强的健壮版本）
  const mapConfigToForm = (configData: any): OrderFormValues => {
    if (!configData) {
      // 对于空配置，返回默认表单值
      return {
        dataSourceType: 'postgres',
        is_default: true,
        enabled: true,
      }
    }

    // 基础验证
    const { valid, errors } = validateConfig(configData)
    if (!valid) {
      console.warn('配置验证警告:', errors)
      // 即使验证失败，也尝试映射可用的字段
    }

    const formValues: OrderFormValues = {
      dataSourceType: configData.type || 'postgres',
      is_default: configData.is_default !== false,
      enabled: configData.enabled !== false,
    }

    const config = configData.config || {}

    const configType = configData.type || 'postgres'
    switch (configType) {
      case 'postgres':
      case 'mysql': {
        const dbConfig = config
        formValues.host = dbConfig.host || 'localhost'
        formValues.port = dbConfig.port || (configType === 'postgres' ? 5432 : 3306)
        formValues.database = dbConfig.database || 'default_orders'
        formValues.username = dbConfig.username || ''
        formValues.password = dbConfig.password || ''

        if (configType === 'postgres') {
          formValues.schema = dbConfig.schema || 'public'
          formValues.ssl_mode = dbConfig.ssl_mode || 'prefer'
        } else {
          formValues.charset = dbConfig.charset || 'utf8mb4'
        }
        break
      }
      case 'rest_api': {
        const apiConfig = config
        formValues.apiUrl = apiConfig.base_url || ''
        formValues.apiKey = apiConfig.api_key || ''
        formValues.authType = apiConfig.auth_type || 'bearer'
        formValues.timeout = apiConfig.timeout || 30
        formValues.retries = apiConfig.retries || 3
        break
      }
      default:
        // 未知类型，但仍尝试映射
        console.warn(`未知的配置类型: ${configType}`)
    }

    return formValues
  }


  const handleTestConnection = async () => {
    setLoading(true)
    try {
      // 获取表单值并构建配置对象
      const values = await form.validateFields()
      const config = buildStorageConfigRequest(values)

      // 调用订单配置测试API
      const response = await orderConfigApi.testConnection(companyId, config)

      if (response?.success) {
        if (response.healthy) {
          messageApi.success(t('modules:order.config.test.success'))
        } else {
          messageApi.warning(t('modules:order.config.test.unhealthy'))
        }
      } else {
        messageApi.error(response?.error || '连接测试失败')
      }
    } catch (error: any) {
      console.error('连接测试错误:', error)
      messageApi.error(error.response?.data?.error || '连接测试失败，请检查配置')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveConfig = async () => {
    setLoading(true)
    try {
      const values = await form.validateFields()
      const config = buildStorageConfigRequest(values)

      const response = await orderConfigApi.saveConfig(companyId, config)

      if (response?.success) {
        messageApi.success(response.message || t('modules:order.config.save.success'))
        // 保存成功后更新缓存
        saveToCache(config)
      } else {
        messageApi.error(response?.error || '保存配置失败')
      }
    } catch (error: any) {
      console.error('保存配置错误:', error)
      messageApi.error(error.response?.data?.error || '保存配置失败')
    } finally {
      setLoading(false)
    }
  }

  // 配置状态指示器组件
  const ConfigStatusIndicator: React.FC<{ state: typeof configState }> = ({ state }) => {
    switch (state) {
      case 'loading':
        return <PageLoading />
      case 'unconfigured':
        return (
          <Alert
            type="info"
            message="未配置数据源"
            description="请填写以下表单配置您的订单数据源"
            showIcon
          />
        )
      case 'error':
        return (
          <Alert
            type="warning"
            message="配置加载失败"
            description="无法加载数据源配置，请刷新页面重试"
            showIcon
          />
        )
      case 'loaded':
        return null // 正常显示表单
      default:
        return null
    }
  }

  // 根据配置状态动态设置initialValues
  const getInitialValues = (): OrderFormValues | undefined => {
    if (configState === 'unconfigured') {
      return {
        dataSourceType: 'postgres',
        host: '',
        port: undefined,
        database: '',
        username: '',
        syncFrequency: 'realtime',
        errorRetryCount: 3,
        is_default: true,
        enabled: true,
      }
    }
    return undefined // 让useEffect处理数据加载
  }

  // 数据源类型选项
  const dataSourceTypes = [
    { value: 'postgres', label: 'PostgreSQL', icon: <DatabaseOutlined /> },
    { value: 'mysql', label: 'MySQL', icon: <DatabaseOutlined /> },
    { value: 'rest_api', label: 'REST API', icon: <ApiOutlined /> },
  ]

  if (loading) {
    return <PageLoading />
  }

  return (
    <div className="order-config-page">
      <ConfigStatusIndicator state={configState} />
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 8 }}>
          {t('modules:order.config.title')}
        </Title>
        <Paragraph type="secondary">
          {t('modules:order.config.subtitle')}
        </Paragraph>
      </div>

      <Form
        form={form}
        layout="vertical"
        className="order-config-form"
        initialValues={getInitialValues()}
      >
        {/* 数据源类型选择 */}
        <Card className="order-config-section">
          <Title level={5} style={{ marginBottom: 16 }}>
            {t('modules:order.config.dataSource.title')}
          </Title>
          <Form.Item
            name="dataSourceType"
            label={t('modules:order.config.dataSource.type')}
            rules={[{ required: true, message: t('modules:order.config.dataSource.typeRequired') }]}
          >
            <Select
              onChange={handleDataSourceTypeChange}
              style={{ width: 200 }}
            >
              {dataSourceTypes.map(type => (
                <Option key={type.value} value={type.value}>
                  <Space>
                    {type.icon}
                    {type.label}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Card>

        {/* 连接配置 */}
        <Card className="order-config-section">
          <Title level={5} style={{ marginBottom: 16 }}>
            {t('modules:order.config.connection.title')}
          </Title>

          {dataSourceType === 'postgres' || dataSourceType === 'mysql' ? (
            <>
              <Form.Item
                name="host"
                label={t('modules:order.config.connection.host')}
                rules={[{ required: true, message: t('modules:order.config.connection.hostRequired') }]}
              >
                <Input placeholder="localhost" />
              </Form.Item>

              <Form.Item
                name="port"
                label={t('modules:order.config.connection.port')}
                rules={[{ required: true, message: t('modules:order.config.connection.portRequired') }]}
              >
                <Input type="number" placeholder={dataSourceType === 'postgres' ? '5432' : '3306'} />
              </Form.Item>

              <Form.Item
                name="database"
                label={t('modules:order.config.connection.database')}
                rules={[{ required: true, message: t('modules:order.config.connection.databaseRequired') }]}
              >
                <Input placeholder="order_db" />
              </Form.Item>

              <Form.Item
                name="username"
                label={t('modules:order.config.connection.username')}
                rules={[{ required: true, message: t('modules:order.config.connection.usernameRequired') }]}
              >
                <Input placeholder="username" />
              </Form.Item>

              <SensitiveFieldInput
                name="password"
                label={t('modules:order.config.connection.password')}
                placeholder="password"
                hasValue={false} // 暂时设置为false，后续需要后端支持has_password字段
                isAdmin={true} // 订单配置只有管理员能访问
                rules={[{ required: true, message: t('modules:order.config.connection.passwordRequired') }]}
                fieldType="password"
              />
            </>
          ) : dataSourceType === 'rest_api' ? (
            <>
              <Form.Item
                name="apiUrl"
                label={t('modules:order.config.connection.apiUrl')}
                rules={[{ required: true, message: t('modules:order.config.connection.apiUrlRequired') }]}
              >
                <Input placeholder="https://api.example.com/orders" />
              </Form.Item>

              <SensitiveFieldInput
                name="apiKey"
                label={t('modules:order.config.connection.apiKey')}
                placeholder="API Key"
                hasValue={false} // 暂时设置为false，后续需要后端支持has_api_key字段
                isAdmin={true} // 订单配置只有管理员能访问
                rules={[{ required: true, message: t('modules:order.config.connection.apiKeyRequired') }]}
                fieldType="apiKey"
              />

              <Form.Item
                name="authType"
                label={t('modules:order.config.connection.authType')}
                rules={[{ required: true, message: t('modules:order.config.connection.authTypeRequired') }]}
              >
                <Select placeholder="选择认证方式">
                  <Option value="api_key">API Key</Option>
                  <Option value="bearer_token">Bearer Token</Option>
                  <Option value="oauth2">OAuth 2.0</Option>
                </Select>
              </Form.Item>
            </>
          ) : null}
        </Card>

        {/* 同步设置 */}
        <Card className="order-config-section">
          <Title level={5} style={{ marginBottom: 16 }}>
            {t('modules:order.config.sync.title')}
          </Title>

          <Form.Item
            name="syncFrequency"
            label={t('modules:order.config.sync.frequency')}
            rules={[{ required: true, message: t('modules:order.config.sync.frequencyRequired') }]}
          >
            <Select style={{ width: 200 }}>
              <Option value="realtime">{t('modules:order.config.sync.realtime')}</Option>
              <Option value="hourly">{t('modules:order.config.sync.hourly')}</Option>
              <Option value="daily">{t('modules:order.config.sync.daily')}</Option>
              <Option value="manual">{t('modules:order.config.sync.manual')}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="errorRetryCount"
            label={t('modules:order.config.sync.retryCount')}
          >
            <Input type="number" min={0} max={10} />
          </Form.Item>
        </Card>

        <Divider />

        {/* 操作按钮 */}
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Space size="large">
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={handleTestConnection}
              size="large"
            >
              {t('modules:order.config.testConnection')}
            </Button>

            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveConfig}
              size="large"
            >
              {t('modules:order.config.saveConfig')}
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  )
}

export default OrderConfigPage